import { isAxiosError, isCancel, type AxiosError } from 'axios';
import { castArray } from 'lodash';
import type { Component, ComputedRef, VNode } from 'vue';
import { computed, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

export type UseErrorAction = {
  text: string;
  as: 'primary' | 'secondary';
} & (
  | {
      handler: () => void;
    }
  | {
      to: RouteLocationRaw;
    }
);

export type UseErrorLevel = 'danger' | 'warning' | 'success';
export type UseErrorMessage = string | VNode | Component;

export interface UseError {
  message: UseErrorMessage;
  messages?: UseErrorMessage[];
  level: UseErrorLevel;
  code: string;
  error?: unknown;
  actions?: UseErrorAction[];
}

export type ErrorTransformer<T> = (error: T) => UseError | undefined;

export type UseErrors = {
  add: (error: UseError) => void;
  collect: (error: unknown) => void;
  clear: () => void;
  use: {
    (code: string | string[]): (error: unknown) => UseError;
    <T>(code: string | string[], handler: (uiError: UseError, error: any) => T): (error: any) => T;
  };
  all: ComputedRef<UseError[]>;
  first: ComputedRef<UseError>;
  last: ComputedRef<UseError>;
  isError: (error: any) => error is UseError;
};

export const axiosTransformer =
  (transformer: ErrorTransformer<AxiosError>): ErrorTransformer<unknown> =>
  (error) => {
    if (isAxiosError(error)) {
      if (isCancel(error)) {
        return;
      }

      return transformer(error);
    }

    return;
  };

export const apiTransformer = (
  transformer: (
    data: { code: string; message: string; status: number; errors?: Record<string, string[]> },
    error: AxiosError,
  ) => UseError | undefined,
): ErrorTransformer<unknown> =>
  axiosTransformer((error) => {
    const data = error.response?.data as
      | {
          code?: string;
          message?: string;
          errors?: Record<string, string[]>;
        }
      | undefined;

    if (data?.message === undefined) {
      return;
    }

    return transformer(
      {
        code: data.code ?? 'API_ERROR',
        message: data.message.replace(/(?<=[A-Za-z])\.(?=[A-Za-z])/g, ' '), // Replace dot between words with a space
        errors: data.errors,
        status: error.response?.status || 0,
      },
      error,
    );
  });

export const validationTransformer: ErrorTransformer<unknown> = apiTransformer((data, error) => {
  const fieldErrors = data.errors ? Object.values(data.errors).flat() : [];

  return {
    message: data.message,
    messages: fieldErrors.length > 0 ? fieldErrors : undefined,
    code: data.code,
    level: 'danger',
    error,
  };
});

const defaultTransformers: ErrorTransformer<unknown>[] = [
  apiTransformer((data, error) => ({
    message: data.message,
    code: data.code,
    level: 'danger',
    error,
  })),
  axiosTransformer((error) => ({
    message: error.message,
    code: error.code || 'AXIOS_ERROR',
    level: 'danger',
    error,
  })),
  (error) => ({
    message: 'Looks like something went wrong...',
    code: 'UNKNOWN_ERROR',
    level: 'danger',
    error,
  }),
];

export const useErrors = (transformers: ErrorTransformer<unknown>[] = []): UseErrors => {
  const errors = ref<UseError[]>([]);

  const clear = () => {
    errors.value = [];
  };

  const add = (error: UseError) => {
    errors.value.push(error);
  };

  const use: {
    (code: string | string[]): (error: unknown) => UseError;
    <T>(code: string, handler: (uiError: UseError, error: any) => T): (error: any) => T;
  } =
    <T>(code: string | string[], handler?: (uiError: UseError, error: any) => T) =>
    (error: any): UseError | T => {
      const codes = castArray(code);

      for (const transformer of [...transformers, ...defaultTransformers]) {
        const uiError = transformer(error);

        if (uiError === undefined) {
          continue;
        }

        if (codes.includes(uiError.code)) {
          return handler ? handler(uiError, error) : uiError;
        }

        break;
      }

      throw error;
    };

  const collect = (error: unknown) => {
    const uiErrors: UseError[] = [];

    [...transformers, ...defaultTransformers].some((transformer) => {
      const uiError = transformer(error);

      if (uiError) {
        uiErrors.push(uiError);
      }

      return uiError;
    });

    clear();

    uiErrors.forEach(add);

    if (uiErrors.some((error) => error.code === 'UNKNOWN_ERROR')) {
      throw error;
    }
  };

  const all = computed(() => errors.value);
  const first = computed(() => errors.value[errors.value.length - 1]);
  const last = computed(() => errors.value[0]);

  return {
    add,
    collect,
    clear,
    use,
    all,
    first,
    last,
    isError: (error: any): error is UseError =>
      'code' in error && 'message' in error && 'level' in error,
  };
};
